import { map, startsWith, values } from 'lodash-es';
import debug from 'debug';
import { Openapi } from './openapi';
import {
  RequestArgs,
  OpenapiConnectionSpec,
  Connection,
  ConnectionSpec,
  openapiConnectionSpec,
} from './types';
import * as core from './core';

const log = debug('itp:core:connections');

function isOpenapiConnectionSpec(value: any): value is OpenapiConnectionSpec {
  return openapiConnectionSpec.safeParse(value).success;
}

export class Connections {
  private readonly items: Dictionary<Connection>;
  private acceptLanguage: string;

  constructor(acceptLanguage: string) {
    this.acceptLanguage = acceptLanguage;
    this.items = {};
  }

  initialize(specs: ConnectionSpec[]) {
    log('initializing connections...');

    const visited: string[] = [],
      promises = map(specs, (cfg) => {
        if (visited.indexOf(cfg.name) > -1) {
          throw new Error(`Connection name should be unique: "${cfg.name}"`);
        }
        visited.push(cfg.name);

        if (isOpenapiConnectionSpec(cfg)) {
          return Openapi.load(cfg, this.acceptLanguage).then((o) => {
            this.items[cfg.name] = o;
            return o;
          });
        }
      });

    return Promise.all(promises).then(() => this);
  }

  get default() {
    return values(this.items)[0];
  }

  getConnection(name: string): Connection {
    log('getConnection (name=%o)', name);

    if (name === 'default') {
      const defaultConn = this.default;
      if (!defaultConn) {
        throw new Error(
          `No default connection available. Connections may not be initialized yet. Available: ${Object.keys(this.items).join(', ') || 'none'}`,
        );
      }
      return defaultConn;
    }

    const result = this.items[name];

    if (!result) {
      throw new Error(
        `Connection not found. name=${name}. Available: ${Object.keys(this.items).join(', ') || 'none'}`,
      );
    }

    return result;
  }

  private defaultArgs(args?: RequestArgs) {
    if (!args) {
      args = {};
    }
    if (!args.language) {
      args.language = this.acceptLanguage;
    }
    return args;
  }

  async request(operation: string, args?: RequestArgs) {
    log('request - args: %o', args);

    const cn = parseConnectionString(operation),
      conn = this.getConnection(cn.connection);

    return await conn.request(cn.op, this.defaultArgs(args));
  }

  async ajax(url: string, args?: RequestArgs) {
    return await core.request(url, this.defaultArgs(args));
  }

  urlFor(op: string, args?: object) {
    const tokens = op.split('/'),
      connectionName = tokens.length === 1 ? 'default' : tokens[0],
      conn = this.getConnection(connectionName) as Openapi;

    let result = conn.urlFor(op, args);

    if (!startsWith(result, 'http')) {
      result = new URL(result, window.origin).toString();
    }

    log('urlFor: %s, (op=%o, args=%o)', result, op, args);
    return result;
  }
}

export function parseConnectionString(connectionString: string) {
  if (
    connectionString.startsWith('http://') ||
    connectionString.startsWith('https://')
  ) {
    try {
      const url = new URL(connectionString);
      return {
        op: connectionString,
        connection: url.protocol,
      };
    } catch (e) {
      // ... continue to old logic
    }
  }

  const parts = connectionString.split('/'),
    op = parts.pop();

  if (!op) {
    throw new Error(`Cannot parse connection string ${connectionString}`);
  }

  return {
    op,
    connection: parts.length ? parts[0] : 'default',
  };
}
